"use client";

import React from "react";
import { Phone, MessageCircle, MapPin, CircleCheck as CheckCircle2, ShieldCheck, Flame } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { PHONE_E164, PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HeroNakheelOvenCleaning() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="heronakheelovencleaning" data-nav="light" ref={ref} dir="rtl" className="relative overflow-hidden bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Main content column */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6 transition-all duration-700" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium text-xs sm:text-sm">
                <MapPin className="size-3.5 text-primary" />
                <span>خدمة مخصصة لسكان حي النخيل بالرياض</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 px-3 py-1 text-xs sm:text-sm text-muted-foreground">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>ضمان فوري وخدمة منزلية</span>
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              أفضل شركة <span className="text-primary">تنظيف أفران حي النخيل</span> وصيانتها بالمنزل
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              تقدم شركة <strong>لمسة احتراف</strong> خدمات متخصصة في تنظيف أفران حي النخيل وصيانتها الفورية
              داخل منزلك في الرياض. نقوم بإزالة الدهون المستعصية، تلميع الأفران الغازية والكهربائية، فحص الشعلات،
              وتغيير قطع الغيار الأصلية بأيدي فنيين محترفين ومعايير أمان عالية.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-xl pt-2">
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-card/60">
                <CheckCircle2 className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium">إزالة الشحوم والزيوت المتراكمة تماماً</span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-card/60">
                <CheckCircle2 className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium">صيانة تسريب الغاز وضبط الشعلات</span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-card/60">
                <CheckCircle2 className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium">تنظيف وتلميع الزجاج الداخلي والخارجي</span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-card/60">
                <CheckCircle2 className="size-5 text-primary shrink-0" />
                <span className="text-sm font-medium">وصول سريع لجميع مربعات حي النخيل</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm">
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-4" />
                  <span>اتصل الآن: {PHONE_LOCAL}</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="gap-2 border-border hover:bg-accent font-semibold shadow-xs">
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-4 text-chart-4" />
                  <span>تواصل عبر واتساب</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Image & Service Visual Card */}
          <div className="lg:col-span-5 relative transition-all duration-700 delay-150" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/nakheel_oven_cleaning_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/nakheel_oven_cleaning_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/nakheel_oven_cleaning_hero.webp-wvc-sizes" alt="تنظيف أفران حي النخيل وصيانتها المنزلية في الرياض - لمسة احتراف" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />

              <div className="p-5 bg-card border-t border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-md bg-primary/10 text-primary">
                      <Flame className="size-5" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-sm sm:text-base text-foreground">
                        خدمة سريعة في حي النخيل
                      </h2>
                      <p className="text-xs text-muted-foreground">فحص وصيانة وتنظيف عميق للفرن في منزلك</p>
                    </div>
                  </div>
                  <Badge variant="default" className="text-xs">
                    متاح اليوم
                  </Badge>
                </div>

                <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span>منطقة الخدمة: حي النخيل، شمال الرياض</span>
                  <a href={PHONE_TEL_HREF} className="text-primary font-medium hover:underline">
                    {PHONE_E164}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}